import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Emblem } from "../../components/PublicLayout";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const field = "w-full bg-[#141414] border border-[#333] text-[#fdfbf7] rounded-sm px-4 py-3 text-sm focus:border-[#d4af37] placeholder:text-[#666]";

function startGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleButton() {
  return (
    <>
      <div className="flex items-center gap-3 my-5">
        <span className="h-px flex-1 bg-[#333]" />
        <span className="text-[11px] tracking-luxe uppercase text-[#666]">or</span>
        <span className="h-px flex-1 bg-[#333]" />
      </div>
      <button type="button" data-testid="google-signin-btn" onClick={startGoogle}
        className="w-full py-3 rounded-sm bg-white text-[#1f1f1f] text-sm font-semibold flex items-center justify-center gap-2.5 hover:bg-[#f2f2f2] transition-colors">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        Continue with Google
      </button>
    </>
  );
}

function dest(role) {
  if (role === "customer") return "/portal";
  if (role === "cleaner") return "/cleaner";
  return "/admin";
}

export function Login() {
  const { login, verify2fa } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const go = (role) => { if (next && (role === "customer" || role === "founder_ceo")) nav(next); else nav(dest(role)); };
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(location.state?.mfa || null);
  const [code, setCode] = useState("");

  const isAdmin = location.pathname === "/admin-login";
  const isStaff = location.pathname === "/staff-login";
  const heading = isAdmin ? "Owner / Admin sign in" : isStaff ? "Staff sign in" : "Sign in";
  const sub = isAdmin
    ? "Private, secure access for the business owner and administrators. Two-factor verification is required."
    : isStaff
    ? "Secure access for Black Diamond staff. Management accounts use two-factor verification."
    : "Access your Black Diamond customer account.";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, pw);
    setBusy(false);
    if (r.ok) { toast.success("Welcome back"); go(r.role); }
    else if (r.mfa) { setMfa({ challenge_id: r.challenge_id, maskedEmail: r.maskedEmail }); toast.message("Enter the 6-digit code we emailed you"); }
    else toast.error(r.error);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await verify2fa(mfa.challenge_id, code.trim());
    setBusy(false);
    if (r.ok) { toast.success("Verified"); go(r.role); }
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-5 noise-overlay">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Emblem variant="full" className="h-24" /></div>
        <div className="bg-[#111] border border-[#333] rounded-sm p-8" data-testid="login-card">
          {!mfa ? (
            <>
              <h1 className="font-display text-3xl text-[#fdfbf7] tracking-tight">{heading}</h1>
              <p className="text-[#a3a3a3] text-sm mt-1">{sub}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="login-email" type="email" placeholder="Email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} />
                <input data-testid="login-password" type="password" placeholder="Password" className={field} value={pw} onChange={(e) => setPw(e.target.value)} />
                <button data-testid="login-submit" disabled={busy} className="btn-gold w-full py-3 rounded-sm">{busy ? "Signing in…" : "Sign in"}</button>
              </form>
              <GoogleButton />
              <p className="text-sm text-[#a3a3a3] mt-5">No account? <Link to="/register" className="text-[#d4af37]">Create one</Link></p>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl text-[#fdfbf7] tracking-tight">Two-factor verification</h1>
              <p className="text-[#a3a3a3] text-sm mt-1">We emailed a 6-digit code to {mfa.maskedEmail || "your email"}. It expires in 10 minutes.</p>
              <form onSubmit={verify} className="mt-6 space-y-4">
                <input data-testid="mfa-code" inputMode="numeric" maxLength={6} placeholder="______" className={`${field} tracking-[0.5em] text-center text-lg`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                <button data-testid="mfa-verify" disabled={busy || code.length < 6} className="btn-gold w-full py-3 rounded-sm">{busy ? "Verifying…" : "Verify & continue"}</button>
              </form>
              <button data-testid="mfa-back" onClick={() => { setMfa(null); setCode(""); }} className="text-xs text-[#666] mt-4 hover:text-[#d4af37]">← Use a different account</button>
            </>
          )}
          <Link to="/" className="text-xs text-[#666] mt-3 inline-block hover:text-[#d4af37]">← Back to website</Link>
        </div>
      </div>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    const r = await register(f);
    setBusy(false);
    if (r.ok) { toast.success("Account created"); nav("/portal"); }
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-5 py-10 noise-overlay">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Emblem variant="full" className="h-24" /></div>
        <div className="bg-[#111] border border-[#333] rounded-sm p-8">
          <h1 className="font-display text-3xl text-[#fdfbf7] tracking-tight">Create account</h1>
          <p className="text-[#a3a3a3] text-sm mt-1">Track quotes, bookings and invoices.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input data-testid="reg-name" placeholder="Full name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} />
            <input data-testid="reg-email" type="email" placeholder="Email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="reg-phone" placeholder="Mobile (optional)" className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            <input data-testid="reg-password" type="password" placeholder="Password" className={field} value={f.password} onChange={(e) => set("password", e.target.value)} />
            <button data-testid="reg-submit" disabled={busy} className="btn-gold w-full py-3 rounded-sm">{busy ? "Creating…" : "Create account"}</button>
          </form>
          <GoogleButton />
          <p className="text-sm text-[#a3a3a3] mt-5">Already have an account? <Link to="/login" className="text-[#d4af37]">Sign in</Link></p>
          <Link to="/" className="text-xs text-[#666] mt-3 inline-block hover:text-[#d4af37]">← Back to website</Link>
        </div>
      </div>
    </div>
  );
}


export function AuthCallback() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: decodeURIComponent(m[1]) });
        window.history.replaceState(null, "", window.location.pathname);
        if (data.mfa_required) {
          nav("/login", { replace: true, state: { mfa: { challenge_id: data.challenge_id, maskedEmail: data.email } } });
          return;
        }
        await refresh();
        const dest = data.role === "customer" ? "/portal" : data.role === "cleaner" ? "/cleaner" : "/admin";
        nav(dest, { replace: true });
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center noise-overlay" data-testid="auth-callback">
      <Loader2 size={44} className="animate-spin text-[#d4af37]" />
      <p className="text-[#cfcabf] mt-5 text-sm">Completing your sign-in…</p>
    </div>
  );
}
